import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import { Toaster } from './components/ui/sonner';
import BrandThemeApplier from './components/BrandThemeApplier';
import FontScaleApplier from './components/FontScaleApplier';
import SupportChatBubble from './components/SupportChatBubble';

import Login from './pages/Login';
import Signup from './pages/Signup';
import Home from './pages/Home';
import Marketplace from './pages/Marketplace';
import StoreSlugRedirect from './pages/StoreSlugRedirect';
import MarketplaceNew from './pages/MarketplaceNew';
import MarketplaceMine from './pages/MarketplaceMine';
import MarketplaceDetail from './pages/MarketplaceDetail';
import VendorDetail from './pages/VendorDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import TrackOrder from './pages/TrackOrder';
import Orders from './pages/Orders';
import Invoice from './pages/Invoice';
import Profile from './pages/Profile';
import Wallet from './pages/Wallet';
import AuthCallback from './pages/AuthCallback';
import PaymentCallback from './pages/PaymentCallback';
import VendorGallery from './pages/VendorGallery';
import LogisticsBooking from './pages/LogisticsBooking';

import AdminLayout from './admin/AdminLayout';
import Dashboard from './admin/Dashboard';
import UsersPage from './admin/UsersPage';
import VendorsPage from './admin/VendorsPage';
import MenuEditor from './admin/MenuEditor';
import OrdersPage from './admin/OrdersPage';
import ETransferPage from './admin/ETransferPage';
import PromosPage from './admin/PromosPage';
import CategoriesPage from './admin/CategoriesPage';
import SettingsAdminPage from './admin/SettingsAdminPage';
import WhatsAppAdminPage from './admin/WhatsAppAdminPage';
import OperationsAdminPage from './admin/OperationsAdminPage';
import MarketplaceListingsPage from './admin/MarketplaceListingsPage';
import ZonesAdminPage from './admin/ZonesAdminPage';
import DeliveryFeesPage from './admin/DeliveryFeesPage';
import PromotionsPage from './admin/PromotionsPage';
import TaxesPage from './admin/TaxesPage';
import ShippingSizesAdminPage from './admin/ShippingSizesAdminPage';
import CategoryPricingAdminPage from './admin/CategoryPricingAdminPage';
import NavStrip from './components/NavStrip';
import MobileBottomNav from './components/MobileBottomNav';
import PushOptInBanner from './components/PushOptInBanner';

import VendorAdminLayout from './vendor-admin/VendorAdminLayout';
import VendorDashboard from './vendor-admin/VendorDashboard';
import StorePage from './vendor-admin/StorePage';
import VendorMenuPage from './vendor-admin/VendorMenuPage';
import VendorOrdersPage from './vendor-admin/VendorOrdersPage';
import VendorPromotionsPage from './vendor-admin/VendorPromotionsPage';
import VendorGiftPoolPage from './vendor-admin/VendorGiftPoolPage';
import VendorSettingsPage from './vendor-admin/VendorSettingsPage';

import DriverOnboarding from './driver/DriverOnboarding';
import DriverLayout from './driver/DriverLayout';
import DriverDashboard from './driver/DriverDashboard';
import DriverDeliveries from './driver/DriverDeliveries';
import DriverAvailableOrders from './driver/DriverAvailableOrders';
import DriverProfile from './driver/DriverProfile';
import DriversAdminPage from './admin/DriversAdminPage';
import CourierApplicationsPage from './admin/CourierApplicationsPage';
import StatusUpdatesPage from './admin/StatusUpdatesPage';
import HeatmapPage from './admin/HeatmapPage';
import ConfigurationPage from './admin/ConfigurationPage';
import CompletedOrdersPage from './admin/CompletedOrdersPage';
import VendorCompletedOrdersPage from './vendor-admin/VendorCompletedOrdersPage';
import VendorTeamPage from './vendor-admin/VendorTeamPage';
import ClaimStorePage from './vendor-admin/ClaimStorePage';
import ReceiptPage from './pages/ReceiptPage';
import DriverRoutePage from './driver/DriverRoutePage';
import DriverEarningsPage from './driver/DriverEarningsPage';

import VendorOnboarding from './onboarding/VendorOnboarding';
import ShopAnywhere from './pages/ShopAnywhere';

const Loading = () => (
  <div className="h-screen w-full flex items-center justify-center text-slate-500">Loading…</div>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) {
    const intent = loc.pathname.startsWith('/driver') ? 'driver'
      : loc.pathname.startsWith('/vendor-onboarding') ? 'vendor'
      : null;
    return <Navigate to={`/login${intent ? `?intent=${intent}&next=${encodeURIComponent(loc.pathname)}` : ''}`} replace />;
  }
  return children;
};

const Public = ({ children }) => {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (user) {
    // Respect ?next= when the user lands on /login or /signup with an intent,
    // so the signup flow doesn't bounce them home before `nav(next)` fires.
    const params = new URLSearchParams(loc.search);
    const next = params.get('next');
    return <Navigate to={next || '/'} replace />;
  }
  return children;
};

// Detect session_id in hash to route to AuthCallback synchronously
const HashRouter = () => {
  const loc = useLocation();
  if (loc.hash && loc.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <NavStrip />
      <Routes>
        <Route path="/login" element={<Public><Login /></Public>} />
      <Route path="/signup" element={<Public><Signup /></Public>} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/receipt/:orderId" element={<ReceiptPage />} />
      <Route path="/payment/callback" element={<Protected><PaymentCallback /></Protected>} />
      <Route path="/" element={<Protected><Home /></Protected>} />
      <Route path="/marketplace" element={<Protected><Marketplace /></Protected>} />
      <Route path="/store/:slug" element={<StoreSlugRedirect />} />
      <Route path="/marketplace/new" element={<Protected><MarketplaceNew /></Protected>} />
      <Route path="/marketplace/mine" element={<Protected><MarketplaceMine /></Protected>} />
      <Route path="/marketplace/item/:id" element={<Protected><MarketplaceDetail /></Protected>} />
      <Route path="/vendor/:id" element={<Protected><VendorDetail /></Protected>} />
      <Route path="/vendor/:id/gallery" element={<Protected><VendorGallery /></Protected>} />
      <Route path="/book/:vendorId" element={<Protected><LogisticsBooking /></Protected>} />
      <Route path="/cart" element={<Protected><Cart /></Protected>} />
      <Route path="/checkout" element={<Protected><Checkout /></Protected>} />
      <Route path="/track/:orderId" element={<Protected><TrackOrder /></Protected>} />
      <Route path="/orders" element={<Protected><Orders /></Protected>} />
      <Route path="/orders/:orderId/invoice" element={<Protected><Invoice /></Protected>} />
      <Route path="/profile" element={<Protected><Profile /></Protected>} />
      <Route path="/wallet" element={<Protected><Wallet /></Protected>} />
      <Route path="/admin" element={<Protected><AdminLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="vendors" element={<VendorsPage />} />
        <Route path="vendors/:vendorId/menu" element={<MenuEditor />} />
        <Route path="orders" element={<OrdersPage />} />
        <Route path="etransfers" element={<ETransferPage /> } />
        <Route path="completed-orders" element={<CompletedOrdersPage />} />
        <Route path="promos" element={<PromosPage />} />
        <Route path="promotions" element={<PromotionsPage />} />
        <Route path="delivery-fees" element={<DeliveryFeesPage />} />
        <Route path="shipping-sizes" element={<ShippingSizesAdminPage />} />
        <Route path="category-pricing" element={<CategoryPricingAdminPage />} />
        <Route path="taxes" element={<TaxesPage />} />
        <Route path="categories" element={<CategoriesPage />} />
        <Route path="drivers" element={<DriversAdminPage />} />
        <Route path="courier-applications" element={<CourierApplicationsPage />} />
        <Route path="status-updates" element={<StatusUpdatesPage />} />
        <Route path="heatmap" element={<HeatmapPage />} />
        <Route path="configuration" element={<ConfigurationPage />} />
        <Route path="settings" element={<SettingsAdminPage />} />
        <Route path="whatsapp" element={<WhatsAppAdminPage />} />
        <Route path="operations" element={<OperationsAdminPage />} />
        <Route path="marketplace-listings" element={<MarketplaceListingsPage />} />
        <Route path="zones" element={<ZonesAdminPage />} />
      </Route>
      <Route path="/vendor-admin" element={<Protected><VendorAdminLayout /></Protected>}>
        <Route index element={<VendorDashboard />} />
        <Route path="store" element={<StorePage />} />
        <Route path="menu" element={<VendorMenuPage />} />
        <Route path="orders" element={<VendorOrdersPage />} />
        <Route path="completed-orders" element={<VendorCompletedOrdersPage />} />
        <Route path="team" element={<VendorTeamPage />} />
        <Route path="promotions" element={<VendorPromotionsPage />} />
        <Route path="gift-pool" element={<VendorGiftPoolPage />} />
        <Route path="claim-store" element={<ClaimStorePage />} />
        <Route path="settings" element={<VendorSettingsPage />} />
      </Route>
      <Route path="/driver/apply" element={<Protected><DriverOnboarding /></Protected>} />
      <Route path="/vendor-onboarding" element={<Protected><VendorOnboarding /></Protected>} />
      <Route path="/shop-anywhere" element={<Protected><ShopAnywhere /></Protected>} />
      <Route path="/driver" element={<Protected><DriverLayout /></Protected>}>
        <Route index element={<DriverDashboard />} />
        <Route path="deliveries" element={<DriverDeliveries />} />
        <Route path="available" element={<DriverAvailableOrders />} />
        <Route path="route" element={<DriverRoutePage />} />
        <Route path="earnings" element={<DriverEarningsPage />} />
        <Route path="profile" element={<DriverProfile />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <MobileBottomNav />
    <PushOptInBanner />
    </>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppProvider>
            <BrandThemeApplier />
            <FontScaleApplier />
            <HashRouter />
            <SupportChatBubble />
            <Toaster position="top-center" />
          </AppProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
